import { useEffect, useState } from 'react';
import MetadataService from '@utility/services/metadata';
import { CommonConstant } from '@utility/constants/common';
import { BrowserUtility } from '@utility/browser-utility';

export interface IMetadata {
  _id: string;
  company_name: string;
  organization_username: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  email_list: string[];
  address: IAddress;
  email: string;
  mode: string | null;
  phone: string;
  theme: string | null;
  tax_information: ITaxInformation[];
  catalog_health: Record<string, ICatalogHealthEntry>;
  logo: ILogo;
  default_marketplace_region_id: string;
  auto_reload: IAutoReload;
  google_catalog_sheet_id: string;
  google_sheets: IGoogleSheet[];
  paused: boolean;
  flagged: boolean;
  on_boarding: IOnBoarding;
  last_login: ILastLogin;
  display_id: string;
  suppliers: ISupplier[];
  shipmentTabs: Record<string, any>;
}

/* Sub-interfaces */

interface IAddress {
  country: string;
}

interface ITaxInformation {
  marketplace_region_id: string;
  tax_field: string;
  tax_value: string;
}

interface ICatalogHealthEntry {
  marketplace_region_id: string;
  overall_score: number;
  total_listings: number;
  total_resolved_listings: number;
  marketplaces: IMarketplace[];
}

interface IMarketplace {
  id: string;
  name: string;
  total_listing: number;
  score: number;
}

interface ILogo {
  bucket_name: string;
  key: string;
}

interface IAutoReload {
  value: boolean;
  threshold: number;
  amount: number;
  secondary_threshold: number;
  secondary_amount: number;
}

interface IGoogleSheet {
  _id: string;
  type: string;
  sheet_id: string;
}

interface IOnBoarding {
  checklist: string[];
}

interface ILastLogin {
  ip: string;
  user_id: string;
  timestamp: string;
}

interface ISupplier {
  _id: string;
  supplier_name: string;
  email?: string;
  lead_time?: number;
  draft_purchase_orders: any[]; // You can type this if needed
}

interface IMetadataReturn {
  metadata?: IMetadata | {};
  loading: boolean;
}

export const metadataHook = (): IMetadataReturn => {
  const [metadata, setMetadata] = useState<IMetadata | {}>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result: IMetadata = await MetadataService.get();
        setMetadata(result);
      } catch {
        setMetadata({});
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return { metadata, loading };
};

export const orgMetadataHook = (isAuthenticated, wmsPortal = false) => {
  const [regions, setRegions] = useState([]);
  const [marketplaces, setMarketplace] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [defaultRegionId, setDefaultRegionId] = useState('');

  const refreshMetadata = () => {
    setRefresh(refresh + 1);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await MetadataService.orgMetadata();
        setMarketplace(result.marketplaces);
        setRegions(result.regions);
        setDefaultRegionId(result.default_marketplace_region_id);
        if (result.default_marketplace_region_id) {
          BrowserUtility.save(
            CommonConstant.currentRegion,
            result.regions.find(
              x => x._id === result.default_marketplace_region_id,
            )?.currency_code,
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && !wmsPortal) {
      fetchData();
    }
  }, [refresh, isAuthenticated, wmsPortal]);

  return {
    regions,
    marketplaces,
    loading,
    refreshMetadata,
    defaultRegionId,
  };
};
